import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Badge } from '@mantine/core';
import { IconBuildingStore, IconChevronRight, IconCheck, IconClock } from '@tabler/icons-react';
import { Reservation, Business, ServiceBooking } from '@turizi/shared';
import { formatPrice } from '@/lib/format-utils';
import { DEFAULT_CURRENCY } from '@turizi/shared';
import NewPaymentOutModal from '../new-payment-out-modal';
import { TagIcon } from '@/lib/tag-utils';
import CardModal from '../card-modal';

export interface PayProvidersModalProps {
    opened: boolean;
    onClose: () => void;
    reservation?: Reservation;
}

export interface ProviderBookingGroup {
    providerId: number;
    provider: Business;
    bookings: ServiceBooking[];
    unpaidBookings: ServiceBooking[];
    totalUnpaidCost: number;
    currency: string;
}


export default function PayProvidersModal({
    opened,
    onClose,
    reservation,
}: PayProvidersModalProps): JSX.Element {
    const { t } = useTranslation();
    const [selectedGroup, setSelectedGroup] = useState<ProviderBookingGroup | null>(null);
    const [paymentModalOpened, setPaymentModalOpened] = useState(false);

    const getProviderName = (booking: ServiceBooking, id: Number) => {
        return booking.service_data.providers.find((p) => p.id === id)?.name || 'error getting name';
    }

    const providerGroups = useMemo(() => {
        const bookings = reservation?.service_bookings || [];
        const groupsMap = new Map<number, ProviderBookingGroup>();

        bookings.forEach((b) => {
            const providerId = b.selected_provider_id || b.selected_provider?.id;
            if (!providerId) return;

            if (!groupsMap.has(providerId)) {
                const providerObj = b.selected_provider || ({
                    id: providerId,
                    name: getProviderName(b, providerId),
                    type: 'business',
                    category: 'business'
                } as Business);

                groupsMap.set(providerId, {
                    providerId,
                    provider: providerObj,
                    bookings: [],
                    unpaidBookings: [],
                    totalUnpaidCost: 0,
                    currency: b.money_out_currency || DEFAULT_CURRENCY,
                });
            }

            const group = groupsMap.get(providerId)!;
            group.bookings.push(b);

            const costAmt = Number(b.money_out_amount || 0);
            if (costAmt > 0 && b.cost_payment_status !== 'paid') {
                group.unpaidBookings.push(b);
                group.totalUnpaidCost += costAmt;
                if (b.money_out_currency) group.currency = b.money_out_currency;
            }
        });

        return Array.from(groupsMap.values());
    }, [reservation?.service_bookings, t]);

    const handleSelectProviderGroup = (group: ProviderBookingGroup) => {
        setSelectedGroup(group);
        setPaymentModalOpened(true);
    };

    const handleCloseNewPayment = () => {
        setPaymentModalOpened(false);
        setSelectedGroup(null);
    };

    return (
        <>
            <Modal
                opened={opened && !paymentModalOpened}
                onClose={onClose}
                title={t('pay_providers')}
                size="lg"
            >
                <div className="space-y-4 py-1">
                    <p className="text-xs text-slate-500 font-medium">
                        {t('select_provider_to_pay')}
                    </p>

                    {providerGroups.length > 0 ? (
                        <div className="space-y-2.5">
                            {providerGroups.map((group) => {
                                const hasPendingCost = group.unpaidBookings.length > 0;

                                return (
                                    <div
                                        key={group.providerId}
                                        onClick={() => handleSelectProviderGroup(group)}
                                        className="group flex items-center justify-between p-3.5 bg-slate-50 hover:bg-blue-50/60 border border-slate-200/80 hover:border-blue-300 rounded-xl transition-all cursor-pointer shadow-2xs"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0 shadow-3xs">
                                                <TagIcon name={group.provider.category || 'business'} size="xs" hideText={true} noBackground={true} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold text-sm text-slate-800 group-hover:text-blue-700 transition-colors truncate">
                                                    {group.provider.name}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                                                    <span>
                                                        {group.bookings.length} {t('services')}
                                                    </span>
                                                    {hasPendingCost ? (
                                                        <Badge color="red" size="xs" variant="light">
                                                            {group.unpaidBookings.length} {t('pending_costs')}
                                                        </Badge>
                                                    ) : (
                                                        <Badge color="green" size="xs" variant="light" leftSection={<IconCheck size={10} />}>
                                                            {t('paid_costs')}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 shrink-0">
                                            {hasPendingCost ? (
                                                <div className="text-right">
                                                    <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                                                        {t('pending_costs')}
                                                    </div>
                                                    <div className="text-sm font-extrabold text-slate-900">
                                                        ${formatPrice(group.totalUnpaidCost)} <span className="text-xs font-semibold text-slate-400">{group.currency}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-right text-xs font-semibold text-emerald-600">
                                                    {t('paid')}
                                                </div>
                                            )}

                                            <div className="w-7 h-7 rounded-full bg-white group-hover:bg-blue-600 text-slate-400 group-hover:text-white border border-slate-200 group-hover:border-blue-600 flex items-center justify-center transition-all shadow-3xs">
                                                <IconChevronRight size={15} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-xl space-y-2">
                            <IconBuildingStore size={28} className="mx-auto text-slate-300" />
                            <div className="text-xs text-slate-500 font-medium">
                                {t('no_pending_provider_costs')}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Pre-populated NewPaymentOutModal inside a Modal dialog */}
            <CardModal
                opened={paymentModalOpened && !!selectedGroup}
                onClose={handleCloseNewPayment}
                size="full"
                header={
                    <div className='flex w-full items-center'>
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0 shadow-3xs">
                                    <TagIcon name={selectedGroup?.provider?.category} size="xs" hideText={true} noBackground={true} />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-bold text-sm text-slate-800 group-hover:text-blue-700 transition-colors truncate">
                                        {selectedGroup?.provider?.name}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                                        <span>
                                            {selectedGroup?.bookings?.length} {t('services')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            >
                {selectedGroup && (
                    <NewPaymentOutModal
                        onClose={handleCloseNewPayment}
                        initialToClient={selectedGroup.provider}
                        initialAffectedServiceBookings={selectedGroup.unpaidBookings.map((b) => Number(b.id))}
                        initialAmount={selectedGroup.totalUnpaidCost > 0 ? selectedGroup.totalUnpaidCost : undefined}
                        initialCurrency={selectedGroup.currency}
                        onSuccess={() => {
                            handleCloseNewPayment();
                            onClose();
                        }}
                    />
                )}
            </CardModal>
        </>
    );
}
